import React, { useState, useCallback, createContext, useContext, useEffect } from 'react';
import { useAuthStore } from 'Application/store';

type AuthContextType = {
    toggleRefresh: (status: boolean) => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { refreshToken } = useAuthStore();

    const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);

    // Refresh every 50 minutes
    const refreshThreshold = 1000 * 60 * 50;

    const toggleRefresh = useCallback(
        (status: boolean) => {
            if (status) {
                const interval = setInterval(async () => {
                    try {
                        await refreshToken();
                        console.log('Token refreshed:');
                    } catch (error) {
                        console.error('Error refreshing token:', error);
                    }
                }, refreshThreshold);

                setRefreshInterval(interval);
            } else {
                if (refreshInterval) {
                    clearInterval(refreshInterval);
                    setRefreshInterval(null);
                }
            }
        },
        [refreshInterval, refreshThreshold]
    );

    // Cleanup the interval when the component unmounts
    useEffect(() => {
        return () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }
        };
    }, [refreshInterval]);

    return <AuthContext.Provider value={{ toggleRefresh }}>{children}</AuthContext.Provider>;
};

// Custom hook to use AuthContext
const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export { AuthProvider, useAuth };
